import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, Search, Bookmark, DollarSign, Calendar } from 'lucide-react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Admin
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Analytics Dashboard
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-12 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass border-white/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Users</CardTitle>
              <Users className="h-4 w-4 text-white/70" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{analytics?.total_users || 0}</div>
              <p className="text-xs text-white/70 mt-1">
                {analytics?.recent_signups_30d || 0} new in last 30 days
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Active Subscriptions</CardTitle>
              <TrendingUp className="h-4 w-4 text-white/70" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{analytics?.active_subscriptions || 0}</div>
              <p className="text-xs text-white/70 mt-1">
                {analytics?.trial_users || 0} on trial
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Searches</CardTitle>
              <Search className="h-4 w-4 text-white/70" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{analytics?.total_searches || 0}</div>
              <p className="text-xs text-white/70 mt-1">
                {analytics?.recent_searches_30d || 0} in last 30 days
              </p>
            </CardContent>
          </Card>

          <Card className="glass border-white/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-white/70" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">£{analytics?.total_revenue?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-white/70 mt-1">
                Monthly recurring
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Most Searched Chemicals */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Most Searched Chemicals
            </CardTitle>
            <CardDescription className="text-white/80">
              Top 10 chemicals analyzed by users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics?.most_searched_chemicals?.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-white/70 font-mono text-sm w-8">
                      #{index + 1}
                    </div>
                    <div className="text-white font-medium">{item.chemical}</div>
                  </div>
                  <div className="text-white/70">{item.count} searches</div>
                </div>
              ))}
              {(!analytics?.most_searched_chemicals || analytics.most_searched_chemicals.length === 0) && (
                <div className="text-white/70 text-center py-8">No search data yet</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-lg text-white">Total Bookmarks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Bookmark className="h-8 w-8 text-white/70" />
                <div className="text-3xl font-bold text-white">{analytics?.total_bookmarks || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-lg text-white">Expired Trials</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Calendar className="h-8 w-8 text-white/70" />
                <div className="text-3xl font-bold text-white">{analytics?.expired_users || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="text-lg text-white">Trial Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-white/70" />
                <div className="text-3xl font-bold text-white">{analytics?.trial_users || 0}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
